import ky, { type KyInstance } from "ky";
import { measureSince } from "./perfTiming";

/**
 * Wire shapes returned by the agent-platform-api session endpoints. Field names follow
 * agent-delivery-fx's `session_projection.py` (the actual serializer) — the OpenAPI spec
 * (agent-platform-api/api/openapi.yaml) currently mis-documents them: sessions carry no
 * `user_id`, and message timestamps are `ts`, not `timestamp`.
 */
export interface AgentSession {
  session_id: string;
  agent_name?: string;
  org_id?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ListSessionsResponse {
  sessions?: AgentSession[];
  /** Pagination cursor; non-null means more pages exist. */
  next_cursor?: string | null;
}

/** Body of `PUT sessions/{id}` (rename; blank titles are rejected with 422). `DELETE sessions/{id}` answers 204 with no body. */
export interface RenameSessionRequest {
  title: string;
}

export interface AgentMessage {
  /**
   * Backend-assigned message id; the authoritative identity for feedback. Explicitly null
   * — not absent — on entries the projection synthesised rather than the model wrote
   * (replayed components, `component_prompt` lines): they aren't rateable messages, so a
   * consumer must treat a null id as "no feedback control", never fall back to another key.
   */
  id?: string | null;
  /**
   * `ui_component` is a replayed GenUI component rather than a text turn — it carries the
   * component fields below and a `[ui:<component>]` placeholder `content` that this client
   * ignores in favour of the component fields (see `toThreadMessage`).
   */
  role: "user" | "assistant" | "system" | "ui_component";
  content?: string;
  /** ISO timestamp; null when the model recorded none. */
  ts?: string | null;
  /** Persisted rating, attached to assistant messages by the history endpoint. */
  feedback?: AgentFeedback;
  /**
   * Set on assistant entries the projection derived rather than the model wrote — currently
   * only `component_prompt` (an interactive component's question, replayed as prose).
   */
  kind?: string;
  /** `ui_component` only: stable component instance id (the emitting tool_call_id). */
  component_id?: string;
  /** `ui_component` only: component name the GenUI registry dispatches on. */
  component?: string;
  props?: Record<string, unknown>;
  schema_version?: string;
  /** `ui_component` only: `"display"`. Interactive components are not replayed as widgets. */
  type?: string;
}

export interface SessionMessagesResponse {
  session_id: string;
  messages?: AgentMessage[];
}

export type FeedbackRating = "positive" | "negative";

export interface AgentFeedback {
  type: FeedbackRating;
}

/**
 * Pre-configured ky instance for the agent endpoints under `baseUrl`
 * (e.g. `https://agent.env7.tigerconnect.com/v1/agents/tiger_agent`). Injects a fresh
 * bearer token per request. ky's defaults (10s timeout, GET retry) are kept — both are
 * safe for the idempotent session GETs. The streaming /invocations transport
 * (./transport.ts) shares this instance but opts out per call: `timeout: false`,
 * `retry: 0`, caller-controlled abort signal.
 */
export function createAgentApi(baseUrl: string, getToken: () => Promise<string>): KyInstance {
  return ky.create({
    prefix: baseUrl,
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          const started = performance.now();
          const token = await getToken();
          measureSince("ask-tiger:token", started);
          request.headers.set("Authorization", `Bearer ${token}`);
        },
      ],
    },
  });
}
